/**
 * Module 35 (WP-35.V1) — Block VISUAL config: schema, typed loader, in-bundle
 * default.
 *
 * The single shipped, editable artifact is `games/blockjam/config/block-visuals.json`
 * (the Block Visuals Studio tab writes it; the free-slide renderer reads it).
 * Exactly like `generation.json`, that file lives OUTSIDE the package `rootDir`
 * (`src`), so the compiled library cannot import it — the parity default is
 * authored here as `DEFAULT_BLOCK_VISUAL_CONFIG` and a drift test
 * (`block-visual-config.test.ts`) asserts the JSON parses to exactly this value.
 *
 * Scope rule (locked 2026-07-21): this config only ever describes how a block is
 * RENDERED — never shapes, gameplay, levels, movement or collision.
 *
 * WP-35.V1 is the walking skeleton: only render-backed knobs exist (every one
 * visibly changes the preview). Later WPs grow the schema category by category
 * (colour/palette · corners · bevel/border · gloss · shadow/glow · seams ·
 * selection) until no rendering constant is hardcoded (WP-35.V9).
 */
import { z } from 'zod';
// ─── Schema ───────────────────────────────────────────────────────────────────
/**
 * Sizes below (`corners.radius`, `glow.radius`) are authored in pixels at this
 * reference cell size and scaled linearly by the renderer for any other cell
 * size, so one config looks the same in the Studio preview and in the game.
 */
export const BASE_CELL = 72;
/**
 * The palette entries the Studio edits (WP-35.V2). Cyan is the 7th colour the
 * generator promotes to full use in WP-35.15; it is authored here from day one so
 * the palette editor is complete. Order is the UI order.
 */
export const PALETTE_COLOUR_IDS = [
    'Red',
    'Blue',
    'Green',
    'Yellow',
    'Purple',
    'Orange',
    'Cyan',
];
const hexColour = z
    .string()
    .regex(/^#[0-9a-fA-F]{6}$/, 'must be a 6-digit hex colour like #e74c3c');
/**
 * Per-block modifiers applied OVER the palette (locked decision 8): a block keeps
 * its Red/Blue/… identity, these only shift how that colour is rendered. 1 (and
 * opacity 1) is always the identity.
 */
const generalSchema = z.object({
    brightness: z.number().min(0.2).max(2),
    /** 0 = fully desaturated grey · 1 = palette · >1 = pushed away from grey. */
    saturation: z.number().min(0).max(2),
    /** Contrast around mid-grey: 0 = flat mid-grey · 1 = palette · >1 = punchier. */
    contrast: z.number().min(0).max(2),
    /** Alpha multiplier for every layer of the block. */
    opacity: z.number().min(0).max(1),
});
const paletteSchema = z.object({
    Red: hexColour,
    Blue: hexColour,
    Green: hexColour,
    Yellow: hexColour,
    Purple: hexColour,
    Orange: hexColour,
    Cyan: hexColour,
});
/**
 * Block-wide geometry (WP-35.V9). `inset` is how far the drawn silhouette sits
 * inside its cells — the gap between neighbouring blocks on the board.
 */
const geometrySchema = z.object({
    inset: z.number().min(0).max(20),
});
/**
 * The block's face material (WP-35.V9): a near-flat fill with a hint of top
 * light and bottom shading, so it reads as a solid moulded surface rather than a
 * strong gradient.
 */
const faceSchema = z.object({
    /** Lightening at the top edge. */
    topLightness: z.number().min(0).max(1),
    /** Darkening at the bottom edge. */
    bottomDarkness: z.number().min(0).max(1),
    /** Where the untinted block colour sits, 0 (top) → 1 (bottom). */
    midStop: z.number().min(0).max(1),
});
/**
 * The ↔/↕ overlay marking an axis-locked (Arrows) block (WP-35.V9). Rendering
 * only — whether a block IS locked is gameplay, decided by the generator.
 */
const arrowsSchema = z.object({
    /**
     * Half-arm length as a fraction of ONE CELL, so the glyph is the same size on
     * every shape (corrected 2026-09-22 — it used to be measured against the
     * block's shorter side, which drew it double on a 2×2, a 2×3 and an L; see
     * `drawArrowOverlay`). The drawn arrow is `2 × length` cells tip to tip.
     */
    length: z.number().min(0).max(1),
    /** Head length as a fraction of the arm. */
    headRatio: z.number().min(0).max(1),
    /** How wide each head splays, as a fraction of the head length. */
    headSpread: z.number().min(0).max(2),
    /** Stroke width at `BASE_CELL`. */
    width: z.number().min(0).max(10),
    opacity: z.number().min(0).max(1),
    /** Darkening from the block colour. */
    darkness: z.number().min(0).max(1),
    /**
     * Lightening applied AFTER `darkness`, so the arrow can be a light tint of the
     * block colour instead of only a dark one. Optional: absent = 0 (as shipped).
     */
    lightness: z.number().min(0).max(1).optional(),
    /**
     * Width of a contrasting outline drawn UNDER the arrow, added on each side of
     * the stroke, in px at `BASE_CELL`. It is what keeps the glyph readable where
     * it crosses a seam or the gap between plates — dark on dark otherwise.
     * Optional: absent / 0 = no outline (as shipped).
     */
    outlineWidth: z.number().min(0).max(8).optional(),
    /**
     * Outline tone from the block colour: negative darkens, positive lightens.
     * Absent = 0.5 (a light tint, the contrast for the default dark arrow).
     */
    outlineTone: z.number().min(-1).max(1).optional(),
    /** Outline alpha, multiplied by `opacity`. Absent = 1. */
    outlineOpacity: z.number().min(0).max(1).optional(),
});
const cornersSchema = z.object({
    /** Corner rounding at `BASE_CELL`, applied to every corner incl. the L's reflex one. */
    radius: z.number().min(0).max(36),
    /** Global scale over `radius` — the quick "rounder/sharper everything" knob. */
    radiusMultiplier: z.number().min(0).max(3),
    /** Extra scale for convex (outer) corners only. */
    outerRadius: z.number().min(0).max(2),
    /** Extra scale for the reflex (inner) corner of a concave shape like the L. */
    innerRadius: z.number().min(0).max(2),
    /** 0 = the shipped quadratic corner · 1 = a true circular arc. */
    smoothness: z.number().min(0).max(1),
    /** 0 = radii as authored · 1 = each corner grows to the most its edges allow. */
    shapeSmoothing: z.number().min(0).max(1),
});
/**
 * The bright rim tracing the whole silhouette (WP-35.V4). `softness` spreads it
 * into stacked outline passes — the layered blur approximation, no filters.
 */
const bevelSchema = z.object({
    /** Stroke width at `BASE_CELL`. */
    width: z.number().min(0).max(12),
    /** 0 = one crisp stroke · >0 = stacked wider/fainter passes around it. */
    softness: z.number().min(0).max(1),
    /** Alpha of the core pass. */
    intensity: z.number().min(0).max(1),
    /** How far the rim is lightened from the block colour (0 = block colour, 1 = white). */
    lightStrength: z.number().min(0).max(1),
});
/**
 * The crisp inner line just inside the bevel (WP-35.V4). It sits at half the
 * bevel width inside the silhouette, so widening the bevel moves it with it.
 */
const borderSchema = z.object({
    width: z.number().min(0).max(8),
    opacity: z.number().min(0).max(1),
    /** Same stacked-pass softening as the bevel. */
    blur: z.number().min(0).max(1),
    /** true = a lightened tint of the block colour · false = the fixed `colour` below. */
    useBlockColour: z.boolean(),
    /** Lightening applied when `useBlockColour` is true. */
    lightStrength: z.number().min(0).max(1),
    /** The fixed border colour used when `useBlockColour` is false. */
    colour: hexColour,
});
/**
 * The specular pill in the block's top-left (WP-35.V5). `gloss` is the soft
 * backing pill; `highlight` is the bright core drawn on top of it.
 */
const glossSchema = z.object({
    /** Alpha of the soft backing pill. */
    opacity: z.number().min(0).max(1),
    /** Pill width as a fraction of a cell (clamped to the block's usable width). */
    width: z.number().min(0).max(2),
    /** Pill height as a fraction of a cell. */
    height: z.number().min(0).max(1),
    /** Offset from the block's padded top-left corner, in px at `BASE_CELL`. */
    offsetX: z.number().min(-40).max(80),
    offsetY: z.number().min(-40).max(80),
    /** How far the soft pill extends beyond the core, in px at `BASE_CELL`. */
    spread: z.number().min(0).max(20),
    /** 0 = one soft pill · >0 = extra stacked, fainter passes around it. */
    blur: z.number().min(0).max(1),
    /** Corner rounding as a fraction of the pill height (0.5 = a full capsule). */
    radius: z.number().min(0).max(0.5),
    /** Bows the pill's long edges into a lens; 0 keeps the straight capsule. */
    curvature: z.number().min(0).max(1),
});
const highlightSchema = z.object({
    /** Alpha of the bright core. */
    intensity: z.number().min(0).max(1),
    /** Core size relative to the gloss pill. */
    size: z.number().min(0).max(2),
    /** Nudges the core within the pill, in px at `BASE_CELL`. */
    offsetX: z.number().min(-40).max(40),
    offsetY: z.number().min(-40).max(40),
    /** 0 = one crisp core · >0 = stacked, fainter passes around it. */
    softness: z.number().min(0).max(1),
    colour: hexColour,
});
/**
 * Internal cell grooves (WP-35.V7) — the "+" on a 2×2, the seams along a bar or
 * an L. They only ever mark edges SHARED by two of the block's own cells; the
 * outer silhouette (and therefore the outline, bevel, gloss and shadows built on
 * it) stays one continuous molded piece no matter what these are set to.
 *
 * `strength` is the Seam-Removal knob: at 0 nothing is drawn and the block reads
 * as a single merged shape.
 */
const seamsSchema = z.object({
    strength: z.number().min(0).max(1),
    /** Groove line width at `BASE_CELL`. */
    width: z.number().min(0).max(8),
    /** Base alpha, before `strength` scales it. */
    opacity: z.number().min(0).max(1),
    /** How far each groove stops short of the cell corners, in px at `BASE_CELL`. */
    inset: z.number().min(0).max(30),
    /** true = a darkened tint of the block colour (shipped) · false = `colour`. */
    useBlockColour: z.boolean(),
    /** Darkening applied when `useBlockColour` is true. */
    darkness: z.number().min(0).max(1),
    colour: hexColour,
});
/**
 * Per-cell surface plates (WP-63.A5b) — the block's one piece of **geometry**.
 *
 * Module 63 measured that shading parameters alone cannot answer Playgama's
 * "the blocks look like a typical AI generated asset": at 300 px, a gloss pill
 * and a bevel are invisible, while a competitor's studs read instantly. Studs
 * read because they are geometry with their own silhouette. This is the same
 * class of feature without being a stud — a panel repeated on every cell,
 * either milled INTO the face (`pocket`, the machined-panel look) or standing
 * proud of it (`raised`, the glossy-candy look).
 *
 * Ships **disabled**, so the shipped render is unchanged until a candidate is
 * chosen. Like `seams`, it only ever draws inside the block's own cells and
 * never touches the outer silhouette, so outline, bevel, gloss and shadows stay
 * continuous whatever this is set to.
 */
const platesSchema = z.object({
    /** false (shipped) = nothing drawn at all, and the block renders as it does today. */
    enabled: z.boolean(),
    /** `pocket` = milled in (light catches the lower edge) · `raised` = proud (it casts). */
    style: z.enum(['pocket', 'raised']),
    /**
     * Distance from the cell edge to the plate on every side facing OUT of the
     * block (the space between a tile and the block's border), in px at `BASE_CELL`.
     */
    inset: z.number().min(0).max(30),
    /**
     * Total space between two neighbouring plates of the same block, in px at
     * `BASE_CELL`. Optional so every existing config keeps rendering unchanged:
     * absent means `2 × inset`, which is what a shared edge always got before the
     * two could be tuned apart.
     */
    gap: z.number().min(0).max(60).optional(),
    /** Plate corner radius at `BASE_CELL`. */
    cornerRadius: z.number().min(0).max(30),
    /** How far the plate face departs from the block face. 0 = invisible. */
    depth: z.number().min(0).max(1),
    /** Offset of the edge pass that sells the direction, in px at `BASE_CELL`. */
    edgeOffset: z.number().min(0).max(8),
    /** Lightening/darkening applied to that edge pass. */
    edgeStrength: z.number().min(0).max(1),
    /** Overall alpha, so a candidate can be tried at part strength. */
    opacity: z.number().min(0).max(1),
});
const glowSchema = z.object({
    /** How far the outer colour glow extends beyond the silhouette, at `BASE_CELL`. */
    radius: z.number().min(0).max(48),
    /** Alpha of the innermost (strongest) glow pass. */
    opacity: z.number().min(0).max(1),
    /** Number of stacked outline passes approximating the blur (no PixiJS filters). */
    passes: z.number().int().min(1).max(8),
    /** true = the glow takes the block's own colour (shipped) · false = `colour`. */
    useBlockColour: z.boolean(),
    /** The fixed glow colour used when `useBlockColour` is false. */
    colour: hexColour,
});
/**
 * Offset drop shadow (WP-35.V6). NOT part of the WP-35.7 look — it ships at
 * `opacity: 0` (off) so the default render is unchanged; raise it to lift blocks
 * off the board. Built from stacked outline passes, like the glow.
 */
const shadowSchema = z.object({
    opacity: z.number().min(0).max(1),
    /** Widens the falloff of the stacked passes; 0 = a hard offset silhouette. */
    blur: z.number().min(0).max(1),
    /** Offset from the block, in px at `BASE_CELL`. */
    offsetX: z.number().min(-40).max(40),
    offsetY: z.number().min(-40).max(40),
    /** How far the shadow extends beyond the silhouette, in px at `BASE_CELL`. */
    spread: z.number().min(0).max(40),
    passes: z.number().int().min(1).max(8),
    colour: hexColour,
});
/**
 * Ambient/contact shadow (WP-35.V6): a tight, un-offset dark halo hugging the
 * silhouette. Also new, also off (`strength: 0`) by default.
 */
const ambientSchema = z.object({
    strength: z.number().min(0).max(1),
    radius: z.number().min(0).max(40),
    colour: hexColour,
});
/**
 * The selected/dragged treatment (WP-35.V8) — a thin rim plus a soft halo, kept
 * deliberately subtle so it enhances the block instead of dominating it.
 * Lift/scale/pulse animation stays deferred to a future Animation category.
 */
const selectionSchema = z.object({
    /** Rim stroke width at `BASE_CELL`. */
    outlineWidth: z.number().min(0).max(10),
    outlineOpacity: z.number().min(0).max(1),
    /** 0 = one crisp rim · >0 = stacked wider/fainter passes under it. */
    outlineBlur: z.number().min(0).max(1),
    /** Rim position relative to the silhouette inset; negative sits just outside. */
    outlineInset: z.number().min(-10).max(10),
    /** How far the halo's outermost pass reaches beyond the silhouette. */
    glowRadius: z.number().min(0).max(40),
    /** How far its innermost (densest) pass reaches. */
    glowInnerRadius: z.number().min(0).max(40),
    /** Alpha of that innermost pass. */
    glowStrength: z.number().min(0).max(1),
    glowPasses: z.number().int().min(1).max(6),
    /** Shared by the rim and the halo. */
    colour: hexColour,
    /**
     * false (shipped) = the halo is filled OVER the block, so it also sits above
     * neighbouring blocks — at high strength it washes the face. true = the halo
     * is drawn behind the block, which reads as a true glow but is occluded by
     * whatever overlaps it. The rim is always drawn last either way.
     */
    haloBehind: z.boolean(),
});
export const blockVisualConfigSchema = z.object({
    schemaVersion: z.literal(2),
    general: generalSchema,
    geometry: geometrySchema,
    face: faceSchema,
    arrows: arrowsSchema,
    palette: paletteSchema,
    corners: cornersSchema,
    bevel: bevelSchema,
    border: borderSchema,
    gloss: glossSchema,
    highlight: highlightSchema,
    shadow: shadowSchema,
    ambient: ambientSchema,
    seams: seamsSchema,
    plates: platesSchema,
    selection: selectionSchema,
    glow: glowSchema,
});
// ─── Loader ───────────────────────────────────────────────────────────────────
/**
 * Parse + validate an untyped config object (the JSON file the Studio wrote, or a
 * fixture). Throws a `ZodError` with a readable path on invalid input.
 */
export function loadBlockVisualConfig(raw) {
    return blockVisualConfigSchema.parse(raw);
}
// ─── Default (the shipped, Studio-tuned look) ─────────────────────────────────
/**
 * Kept in sync with `games/blockjam/config/block-visuals.json` — the drift test
 * enforces `loadBlockVisualConfig(json)` deep-equals this value.
 *
 * These numbers were tuned by the user in `/dev/blockjam` → Block Visuals on
 * 2026-07-23 and adopted as the shipped default, superseding the WP-35.7
 * renderer literals this config was originally seeded from. Re-tuned and frozen
 * 2026-09-23 as the Gloss-tile look for Playgama: raised tiles with their own
 * gap setting, and a block-tinted arrow with a light outline so it reads where it
 * crosses the gap between tiles.
 */
export const DEFAULT_BLOCK_VISUAL_CONFIG = loadBlockVisualConfig({
    schemaVersion: 2,
    general: {
        brightness: 0.85,
        saturation: 1,
        contrast: 1.1,
        opacity: 1,
    },
    geometry: {
        inset: 2.5,
    },
    face: {
        topLightness: 0,
        bottomDarkness: 0,
        midStop: 0.48,
    },
    arrows: {
        length: 0.41,
        headRatio: 0.25,
        headSpread: 0.85,
        width: 2.2,
        opacity: 1,
        darkness: 0.5,
        lightness: 0,
        outlineWidth: 2,
        outlineTone: 0.5,
        outlineOpacity: 1,
    },
    // The shipped hexes — now the ONLY definition of a block colour (WP-36.3
    // deleted the duplicate `BLOCK_COLOURS` map).
    //
    // Cyan was `#1abc9c`, a teal that measured only **ΔE2000 13.2** from Green
    // against the shipped `general` modifiers — under the ~15 threshold where two
    // fills stop reading as different colours, which is what made them
    // indistinguishable on the board. `#42def0` is a true cyan: Green/Cyan rises to
    // **31.8** and Cyan stops being part of any tight pair (the palette's closest
    // pair becomes the pre-existing Red/Orange at 19.0, untouched here).
    // `palette-separation.test.ts` holds the floor.
    palette: {
        Red: '#e74c3c',
        Blue: '#3498db',
        Green: '#2ecc71',
        Yellow: '#f1c40f',
        Purple: '#9b59b6',
        Orange: '#e67e22',
        Cyan: '#42def0',
    },
    corners: {
        radius: 10,
        radiusMultiplier: 1,
        outerRadius: 1,
        innerRadius: 0.5,
        smoothness: 1,
        shapeSmoothing: 0,
    },
    bevel: {
        width: 1,
        softness: 0.3,
        intensity: 0.29,
        lightStrength: 0.33,
    },
    border: {
        width: 1,
        opacity: 1,
        blur: 0,
        useBlockColour: true,
        lightStrength: 0.8,
        colour: '#000000',
    },
    gloss: {
        opacity: 0,
        width: 0.5,
        height: 0.15,
        offsetX: 4.5,
        offsetY: 5,
        spread: 0,
        blur: 0,
        radius: 0.5,
        curvature: 0,
    },
    highlight: {
        intensity: 0,
        size: 1,
        offsetX: 1,
        offsetY: 0,
        softness: 0,
        colour: '#ffffff',
    },
    shadow: {
        opacity: 0,
        blur: 0.55,
        offsetX: 0,
        offsetY: 6,
        spread: 0,
        passes: 1,
        colour: '#000000',
    },
    ambient: {
        strength: 0,
        radius: 0,
        colour: '#ffffff',
    },
    seams: {
        strength: 1,
        width: 1,
        opacity: 0.22,
        inset: 6,
        useBlockColour: true,
        darkness: 0.34,
        colour: '#000000',
    },
    // Gloss-tile, tuned in the Studio and frozen 2026-09-23: a raised tile on
    // every cell, with the gap between tiles set apart from the border space.
    plates: {
        enabled: true,
        style: 'raised',
        inset: 8,
        gap: 8,
        cornerRadius: 9,
        depth: 0.2,
        edgeOffset: 1.2,
        edgeStrength: 0.5,
        opacity: 1,
    },
    selection: {
        outlineWidth: 0.5,
        outlineOpacity: 1,
        outlineBlur: 1,
        outlineInset: 0,
        glowRadius: 6,
        glowInnerRadius: 6,
        glowStrength: 0.05,
        glowPasses: 1,
        colour: '#ffffff',
        haloBehind: false,
    },
    glow: {
        radius: 13,
        opacity: 0,
        passes: 4,
        useBlockColour: true,
        colour: '#ffffff',
    },
});
